import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  Dispatch,
  SetStateAction,
} from 'react'
import classNames from 'classnames'
import { Shirt, RotateCw, EyeOffIcon, EyeIcon } from 'lucide-react'
import { isMobile } from 'react-device-detect'
import { AvatarType, ThemeType, aiStudioService } from 'service/aiStudio'
import { useUploadFile } from 'hooks/query/aiStudio/useUploadFile'
import { useUpdateGarmentInfo } from 'hooks/query/aiStudio/useUpdateGarmentInfo'
import { ClothingGuideModal } from '../ClothingGuideModal'
import { FullImageModal } from '../FullImageModal'
import { ReactComponent as Pencil } from 'svg/u_edit-alt.svg'
import {
  ClothingData,
  SynthesisResult,
} from 'pages/ai-studio/tabs/FashionEditorTab/type/type'
import { v4 as uuidv4 } from 'uuid'
import { convertStorageUrlToCdn } from 'utils/common'
import { ImageSlideModal } from 'components/modals/ImageSlideModal'
import { useGetGarmentSynthesisRequestStatus } from 'hooks/query/aiStudio/useGetGarmentSynthesisRequestStatus'
interface CompositionResultStepProps {
  selectedAvatar: AvatarType | null
  selectedTheme: ThemeType[] | null
  clothingData: ClothingData
  setClothingData: Dispatch<SetStateAction<ClothingData>>
  onGoToStep?: (step: number) => void
  onGeneratingChange?: (isGenerating: boolean) => void
}

// 비율을 유지하며 9:16 캔버스에 맞게 이미지 크기 계산
const calculateImageSize = (
  imgWidth: number,
  imgHeight: number,
  canvasWidth: number,
  canvasHeight: number,
) => {
  const imgRatio = imgWidth / imgHeight
  const canvasRatio = canvasWidth / canvasHeight

  let drawWidth, drawHeight, offsetX, offsetY

  if (imgRatio > canvasRatio) {
    // 이미지가 캔버스보다 가로가 더 긴 경우 - 가로를 캔버스에 맞춤
    drawWidth = canvasWidth
    drawHeight = canvasWidth / imgRatio
    offsetX = 0
    offsetY = (canvasHeight - drawHeight) / 2
  } else {
    // 이미지가 캔버스보다 세로가 더 긴 경우 - 세로를 캔버스에 맞춤
    drawHeight = canvasHeight
    drawWidth = canvasHeight * imgRatio
    offsetX = (canvasWidth - drawWidth) / 2
    offsetY = 0
  }

  return { drawWidth, drawHeight, offsetX, offsetY }
}

// 9:16 비율로 이미지 합성하는 함수 (비율 유지)
const combineImages = async (
  topImageUrl: string,
  bottomImageUrl?: string,
): Promise<Blob> => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')!

    // 9:16 비율 (720x1280)
    canvas.width = 720
    canvas.height = 1280

    const topImg = new Image()
    const bottomImg = bottomImageUrl ? new Image() : null

    topImg.crossOrigin = 'anonymous'
    if (bottomImg) bottomImg.crossOrigin = 'anonymous'

    let loadedImages = 0
    const totalImages = bottomImg ? 2 : 1

    const checkComplete = () => {
      loadedImages++
      if (loadedImages === totalImages) {
        // 배경을 흰색으로 설정
        ctx.fillStyle = '#FFFFFF'
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        if (bottomImg) {
          // 상의/하의 합성 - 상의 2/5, 하의 3/5 비율
          const topCanvasHeight = canvas.height * 0.4
          const bottomCanvasHeight = canvas.height * 0.6

          // 상의 크기 계산 및 그리기
          const topSize = calculateImageSize(
            topImg.width,
            topImg.height,
            canvas.width,
            topCanvasHeight,
          )
          ctx.drawImage(
            topImg,
            topSize.offsetX,
            topSize.offsetY,
            topSize.drawWidth,
            topSize.drawHeight,
          )

          // 하의 크기 계산 및 그리기 (하단 절반에 배치)
          const bottomSize = calculateImageSize(
            bottomImg.width,
            bottomImg.height,
            canvas.width,
            bottomCanvasHeight,
          )
          ctx.drawImage(
            bottomImg,
            bottomSize.offsetX,
            topCanvasHeight + bottomSize.offsetY,
            bottomSize.drawWidth,
            bottomSize.drawHeight,
          )
        } else {
          // 원피스 - 전체 캔버스에 비율 유지하며 맞춤
          const size = calculateImageSize(
            topImg.width,
            topImg.height,
            canvas.width,
            canvas.height,
          )
          ctx.drawImage(
            topImg,
            size.offsetX,
            size.offsetY,
            size.drawWidth,
            size.drawHeight,
          )
        }

        canvas.toBlob(
          (blob) => {
            resolve(blob!)
          },
          'image/jpeg',
          0.9,
        )
      }
    }

    topImg.onload = checkComplete
    if (bottomImg) {
      bottomImg.onload = checkComplete
    }

    topImg.src = topImageUrl
    if (bottomImg && bottomImageUrl) {
      bottomImg.src = bottomImageUrl
    }
  })
}

export const CompositionResultStep: React.FC<CompositionResultStepProps> = ({
  selectedAvatar,
  selectedTheme,
  clothingData,
  setClothingData,
  onGoToStep,
  onGeneratingChange,
}) => {
  const { mutateAsync: uploadSingle } = useUploadFile()
  const updateGarmentInfoMutation = useUpdateGarmentInfo()
  const [isGenerating, setIsGenerating] = useState(false)
  const [requestStatus, setRequestStatus] = useState<string>('')
  const [isGuideModalOpen, setIsGuideModalOpen] = useState(false)
  const [compositionError, setCompositionError] = useState(false)
  const [selectedVariation, setSelectedVariation] = useState(0)
  const [currentRequestId, setCurrentRequestId] = useState<number | null>(null)
  const [isFullModalOpen, setIsFullModalOpen] = useState(false)
  const [isHover, setIsHover] = useState(false)
  const [isImageSlideModalOpen, setIsImageSlideModalOpen] = useState(false)
  const [synthesisResults, setSynthesisResults] = useState<any[]>([])

  const { data, isLoading, refetch } =
    useGetGarmentSynthesisRequestStatus(currentRequestId)
  const clothingDataRef = useRef(clothingData)

  // AI 생성 요청 전용 함수 (합성된 이미지를 사용)
  const requestAIGeneration = useCallback(
    async (
      combinedImageUrls: { url: string; sampleImageId: number | null }[],
    ) => {
      const currentData = clothingDataRef.current
      if (!selectedAvatar) {
        console.log(
          '❌ Missing requirements - selectedAvatar:',
          !!selectedAvatar,
        )
        return
      }

      console.log('⏳ Starting AI generation...')
      setIsGenerating(true)

      try {
        // 1. 의류 정보 업데이트 (garment-synthesis-requests 전에 실행)
        const updatePromises = []
        
        if (currentData.topImageId && currentData.topParsedGarmentInfo) {
          updatePromises.push(
            updateGarmentInfoMutation.mutateAsync({
              imageId: currentData.topImageId,
              params: { editedGarmentInfo: currentData.topParsedGarmentInfo }
            })
          )
        }
        
        if (currentData.bottomImageId && currentData.bottomParsedGarmentInfo) {
          updatePromises.push(
            updateGarmentInfoMutation.mutateAsync({
              imageId: currentData.bottomImageId,
              params: { editedGarmentInfo: currentData.bottomParsedGarmentInfo }
            })
          )
        }
        
        // 모든 의류 정보 업데이트 완료 대기
        if (updatePromises.length > 0) {
          console.log('⏳ Updating garment info before synthesis...')
          await Promise.all(updatePromises)
          console.log('✅ Garment info updated successfully')
        }
        
        // 2. 업로드된 이미지 ID 배열 생성
        const uploadedImageIds: {imageId:number, garmentType:string}[] = []
        if (currentData.topImageId) {
          uploadedImageIds.push({imageId:currentData.topImageId,garmentType:"top"})
        }

        if (currentData.bottomImageId) {
        uploadedImageIds.push({imageId:currentData.bottomImageId,garmentType:"bottom"})
        }

        // 옷 타입 결정
        const clothesType = currentData.category || 'top'
        const selectedSampleImageIds = currentData.samples.map((el) => el.id)

        // 옷 설명 생성
        const clothesDescriptions: { top?: string; bottom?: string } = {}
        if (currentData.topDescription) {
          clothesDescriptions.top = currentData.topDescription
        }
        if (currentData.bottomDescription) {
          clothesDescriptions.bottom = currentData.bottomDescription
        }

        console.log({
          avatarId: selectedAvatar.id,
          selectedThemeIds: selectedTheme?.map((el) => el.id) ?? [],
          selectedSampleImageIds,
          uploadedImageIds,
          clothesType,
          clothesDescriptions,
          combinedImageUrls: combinedImageUrls,
        })

        // Synthesis Request API 호출
        const synthesisResponse =
          await aiStudioService.garmentSynthesisRequests({
            avatarId: selectedAvatar.id,
            selectedThemeIds: selectedTheme?.map((el) => el.id) ?? [],
            selectedSampleImageIds,
            uploadedImageIds,
            clothesType,
            clothesDescriptions,
            combinedImageUrls,
            garmentPromptTemplate: '',
            aspectRatio: currentData.aspectRatio,
          })
        console.log(synthesisResponse, 'garmentSynthesisResponse')

        // APIResponse 구조에서 데이터 추출
        let responseData
        if ('data' in synthesisResponse && synthesisResponse.data) {
          responseData = synthesisResponse.data
        } else {
          responseData = synthesisResponse as any
        }

        if (!responseData || !responseData.id) {
          // No data received from synthesis request
          setIsGenerating(false)
          return
        }
        setCurrentRequestId(responseData.id)
      } catch (error) {
        console.log('garmentSynthesisRequests failed:', error)
        // Error during AI generation
        setCompositionError(true)
        setIsGenerating(false)
      }
    },
    [selectedAvatar],
  )

  const handleRegenerate = async () => {
    console.log('🔄 Regenerate button clicked')

    setCurrentRequestId(null)
    setClothingData({
      ...clothingDataRef.current,
      generatedImages: [], // AI 결과만 초기화
    })

    // 기존 combinedImage를 사용해서 AI 생성 요청만 다시 보내기
    const currentData = clothingDataRef.current
    console.log(
      '📸 Current combinedImage:',
      currentData.combinedImage ? 'EXISTS' : 'NULL',
    )
    if (currentData.combinedImage && currentData.combinedImage.length > 0) {
      console.log('🚀 Requesting AI generation with existing combined image')

      await requestAIGeneration(currentData.combinedImage)
    } else {
      console.log('❌ No combinedImage found for regeneration')
    }
  }

  useEffect(() => {
    if (currentRequestId) {
      console.log('refetch start:', currentRequestId)
      refetch()
    }
  }, [currentRequestId])

  useEffect(() => {
    if (data && data?.status === 'garment_completed') {
      const { status, garmentJobs, id } = data
      setRequestStatus(data?.status)

      let resultImages: any[] = []
      let newSynthesisResults: SynthesisResult[] = []
      if (garmentJobs && Array.isArray(garmentJobs)) {
        const completedResults = garmentJobs.filter(
          (garmentJob) =>
            garmentJob.resultImageUrl && garmentJob.status === 'completed',
        )
        resultImages = completedResults.map((el) => {
          return { id: el.id, imgUrl: el.resultImageUrl }
        })
      }

      // synthesisResults를 별도 state에 저장
      setSynthesisResults(garmentJobs)

      // 현재 clothingData를 참조하여 업데이트
      setClothingData({
        ...clothingDataRef.current,
        generatedImages: resultImages,
        selectedGarmentJobId: garmentJobs[0].id,
        garmentRequestId: id,
        sampleRequestStatus: status,
      })

      setIsGenerating(false)
      setCurrentRequestId(null)
    } else if (data && data?.status === 'garment_failed') {
      const { status } = data
      setRequestStatus(status)

      setIsGenerating(false)
      setCurrentRequestId(null)
      setClothingData({
        ...clothingDataRef.current,
        sampleRequestStatus: status,
      })
    } else {
    }
  }, [data])

  // clothingDataRef를 항상 최신 상태로 업데이트
  useEffect(() => {
    clothingDataRef.current = clothingData
  }, [clothingData])

  // isGenerating 상태 변경을 부모에게 알림
  useEffect(() => {
    if (onGeneratingChange) {
      onGeneratingChange(isGenerating)
    }
  }, [isGenerating, onGeneratingChange])

  // 새로운 이미지가 선택되면 에러 상태 초기화
  useEffect(() => {
    setCompositionError(false)
  }, [clothingData.topImage, clothingData.bottomImage, clothingData.category])

  // 컴포넌트 마운트 시 합성 실행 (이미 결과가 있으면 스킵)
  useEffect(() => {
    // 이미 생성된 결과가 있으면 재생성하지 않음
    if (clothingData.generatedImages.length > 0) {
      return
    }

    const performComposition = async () => {
      if (!selectedAvatar) return

      const currentData = clothingDataRef.current
      setCurrentRequestId(null)
      setIsGenerating(true)

      try {
        const { category, topImage, bottomImage, isUseSample, samples } =
          currentData
        let combinedImgs: any[] = []

        if (isUseSample) {
          // 샘플 모드: 배열인 쪽을 기준으로 합성
          if (category === 'bottom' && samples) {
            // 상의가 샘플 배열
            const combinedResults = []
            for (const sampleTop of samples) {
              const blob = await combineImages(
                sampleTop.imageUrl,
                typeof bottomImage === 'string' ? bottomImage : undefined,
              )
              combinedResults.push({ blob, sampleImageId: sampleTop.id })
            }
            // 모든 합성 결과를 업로드
            const uploadResults = await Promise.all(
              combinedResults.map((item, i) =>
                uploadSingle(
                  new File([item.blob], `composed-${uuidv4()}-${i}.jpg`, {
                    type: 'image/jpeg',
                  }),
                ).then((result) => ({
                  url: result.fileUrl,
                  sampleImageId: item.sampleImageId,
                })),
              ),
            )
            // combinedImage를 새로운 구조로 저장
            setClothingData({
              ...currentData,
              combinedImage: uploadResults,
            })
            combinedImgs = uploadResults
          } else if (
            category === 'top' &&
            samples &&
            typeof topImage === 'string'
          ) {
            // 하의가 샘플 배열
            const combinedResults = []
            for (const sampleBottom of samples) {
              const blob = await combineImages(topImage, sampleBottom.imageUrl)
              combinedResults.push({ blob, sampleImageId: sampleBottom.id })
            }

            // 모든 합성 결과를 업로드
            const uploadResults = await Promise.all(
              combinedResults.map((item, i) =>
                uploadSingle(
                  new File([item.blob], `composed-${uuidv4()}-${i}.jpg`, {
                    type: 'image/jpeg',
                  }),
                ).then((result) => ({
                  url: result.fileUrl,
                  sampleImageId: item.sampleImageId,
                })),
              ),
            )
            // combinedImage를 새로운 구조로 저장
            setClothingData({
              ...currentData,
              combinedImage: uploadResults,
            })
            combinedImgs = uploadResults
          }
        } else {
          // 기존 단일 합성 로직
          const combinedBlob = await combineImages(
            typeof currentData.topImage !== 'object'
              ? currentData.topImage
              : '',
            typeof currentData.bottomImage !== 'object'
              ? currentData.bottomImage
              : '',
          )

          // 합성된 이미지를 업로드
          const combinedFile = new File(
            [combinedBlob],
            `composed-${uuidv4()}.jpg`,
            {
              type: 'image/jpeg',
            },
          )

          const result = await uploadSingle(combinedFile)
          const uploadResult = {
            url: result.fileUrl,
            sampleImageId: null,
          }
          setClothingData({
            ...currentData,
            combinedImage: [uploadResult],
          })
          combinedImgs = [uploadResult]
        }
        // AI 생성 요청
        await requestAIGeneration(combinedImgs)
      } catch (error) {
        console.log(error)
        // Error during composition
        setIsGenerating(false)
        setCompositionError(true)
      }
    }

    performComposition()
  }, [selectedAvatar, uploadSingle, setClothingData, requestAIGeneration])

  const handleReplaceClothing = () => {
    // 3단계로 돌아가기
    if (onGoToStep) {
      onGoToStep(3)
    }
  }

  const handleShowFullImage = () => {
    setIsFullModalOpen(true)
  }

  const handleShowImageSlide = () => {
    setIsImageSlideModalOpen(true)
  }

  // 현재 선택된 이미지의 jobId 가져오기
  const getCurrentJobId = (): number | null => {
    if (synthesisResults && synthesisResults[selectedVariation]) {
      return synthesisResults[selectedVariation].id
    }
    return null
  }

  if (isMobile) {
    // 모바일 전용 레이아웃 (새로운 디자인)
    return (
      <div className="flex h-full flex-col gap-4 overflow-y-auto">
        {/* 제목과 설명 섹션 */}
        <div className="flex flex-col gap-1">
          <h3 className="text-[18px] font-bold text-[#2E343D]">
            스타일 확인하기
          </h3>
          <p className="text-base text-[#2E343D]">
            모델이 옷을 잘 입었나요? 확인 후 마음에 드는 이미지를 선택해주세요.
          </p>
        </div>

        {/* 메인 컨텐츠 영역 */}
        <div className="flex flex-col gap-[10px]">
          {isGenerating ? (
            <>
              <div className="flex justify-between">
                {/* 이미지 그리드 스켈레톤 */}
                <div className="relative h-[351px] w-[198px] overflow-hidden rounded-[10px] bg-gradient-to-br from-sky-200 via-indigo-300 to-purple-400">
                  <div className="h-full w-full animate-skeleton bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
                </div>{' '}
                <div className="flex w-[135px] flex-col justify-between">
                  <div className="flex flex-wrap gap-1">
                    {Array.from({ length: 2 }, (_, index) => (
                      <div
                        key={index}
                        className="relative h-[115px] w-[65px] overflow-hidden rounded-[10px] bg-gradient-to-br from-sky-200 via-indigo-300 to-purple-400"
                      >
                        <div className="h-full w-full animate-skeleton bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
                      </div>
                    ))}
                  </div>

                  {/* 로딩 상태 텍스트 */}
                  <div className="mt-4 flex flex-col items-center gap-2">
                    {currentRequestId && (
                      <p className="text-xs text-[#8F929B]">
                        예상 시간: 10-30초
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* 이미지 그리드 */}
              <div className="flex items-start justify-between gap-1">
                {clothingData.generatedImages.length > 0 && (
                  <>
                    <div className="relative">
                      <img
                        src={convertStorageUrlToCdn(
                          clothingData.generatedImages[selectedVariation]
                            .imgUrl,
                        )}
                        alt="생성된 스타일 이미지"
                        className="h-[351px] w-[198px] shrink-0 object-cover"
                        onClick={() => {
                          handleShowImageSlide()
                        }}
                        onMouseOver={() => {
                          setIsHover(true)
                        }}
                        onMouseLeave={() => {
                          setIsHover(false)
                        }}
                      />
                      {/* 전체보기 버튼 */}
                    </div>
                    <div className="grid grid-cols-2 items-start gap-[5px]">
                      {clothingData.generatedImages.length > 0 &&
                        clothingData.generatedImages.map((imageUrl, index) => (
                          <div
                            key={index}
                            className={classNames(
                              'h-[115px] w-[65px] cursor-pointer rounded-[8px] transition-all',
                              {
                                'relative outline outline-[4px] outline-main':
                                  selectedVariation === index,
                                'border-transparent':
                                  selectedVariation !== index,
                              },
                            )}
                            onClick={() => {
                              setSelectedVariation(index)
                              setClothingData((prev) => {
                                return {
                                  ...prev,
                                  selectedGarmentJobId: imageUrl.id,
                                }
                              })
                            }}
                          >
                            <img
                              src={convertStorageUrlToCdn(imageUrl.imgUrl)}
                              alt={`변형 이미지 ${index + 1}`}
                              className="h-full w-full rounded-[4px] object-cover"
                            />
                            {selectedVariation === index && (
                              <div className="absolute bottom-0 left-0 flex h-[28px] w-full items-center justify-center bg-main font-bold text-white">
                                <span>선택</span>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                  </>
                )}
              </div>

              {clothingData.generatedImages.length === 0 &&
                (requestStatus === 'failed' || compositionError) && (
                  <div className="flex h-[142px] w-full flex-col items-center justify-center gap-[10px] rounded-[10px] bg-[#2E343D] p-5">
                    <EyeOffIcon className="h-8 w-8 text-white" />
                    <div className="text-center">
                      <div className="mb-1 text-sm font-bold text-white">
                        생성 실패
                      </div>
                      <div className="text-sm text-[#B6BBC6]">
                        옷 이미지에 문제가 생긴 것 같아요.
                        <br />옷 사진을 교체해보세요!
                      </div>
                    </div>
                  </div>
                )}

              {/* 버튼 섹션 */}
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleReplaceClothing}
                  className="flex h-[40px] w-full items-center justify-center gap-1 rounded-[10px] bg-[#EEF1F7] px-4 py-2 text-sm font-bold text-[#5E6068] transition-colors hover:bg-[#E5E7EB]"
                >
                  <Shirt size={16} />옷 사진 교체
                </button>
                <button
                  onClick={() => {
                    if (onGoToStep) {
                      onGoToStep(4)
                    }
                  }}
                  className="flex h-[40px] w-full items-center justify-center gap-1 rounded-[10px] bg-[#EEF1F7] px-4 py-2 text-sm font-bold text-[#5E6068] transition-colors hover:bg-[#E5E7EB]"
                >
                  <Pencil />옷 특징 설명 수정
                </button>
                {(compositionError ||
                  clothingData.sampleRequestStatus === 'failed') && (
                  <button
                    onClick={handleRegenerate}
                    className="flex h-[40px] w-full items-center justify-center gap-1 rounded-[10px] bg-[#EEF1F7] px-4 py-2 text-sm font-bold text-[#5E6068] transition-colors hover:bg-[#E5E7EB]"
                  >
                    <RotateCw size={16} />
                    재생성
                  </button>
                )}
              </div>

              {/* 가이드 텍스트 */}
              <div className="flex flex-col items-start">
                <p className="text-sm font-bold leading-tight text-[#FD6767]">
                  💃🏻
                  <br />
                  <span className="text-[#2E343D]">
                    혹시 옷 입히기
                    <br />
                    어려우세요?
                  </span>
                  <br />
                  <button
                    onClick={() => setIsGuideModalOpen(true)}
                    className="cursor-pointer text-[#FD6767] hover:underline"
                  >
                    이것부터 확인하세요! &gt;
                  </button>
                </p>
              </div>
            </>
          )}
        </div>

        {/* 클로딩 가이드 모달 */}
        <ClothingGuideModal
          isOpen={isGuideModalOpen}
          onClose={() => setIsGuideModalOpen(false)}
        />

        {/* 전체 이미지 모달 */}
        <FullImageModal
          isOpen={isFullModalOpen}
          onClose={() => setIsFullModalOpen(false)}
          id={getCurrentJobId()}
        />

        {/* 이미지 슬라이드 모달 */}
        <ImageSlideModal
          isOpen={isImageSlideModalOpen}
          onClose={() => setIsImageSlideModalOpen(false)}
          images={clothingData.generatedImages.map((el) => {
            return el.imgUrl
          })}
          initialIndex={selectedVariation}
        />
      </div>
    )
  }

  // 데스크톱 레이아웃 (새로운 디자인)
  return (
    <div className="flex h-full flex-col gap-4">
      {/* 제목과 설명 섹션 */}
      <div className="flex w-[578px] flex-col justify-center gap-2">
        <h3 className="text-[18px] font-bold text-[#2E343D]">
          스타일 확인하기
        </h3>
        <p className="text-base text-[#2E343D]">
          모델이 옷을 잘 입었나요? 확인 후 마음에 드는 이미지를 선택해주세요.
        </p>
      </div>

      {/* 메인 컨텐츠 영역 */}
      <div className="flex-1 rounded-[10px] bg-[#F6F8FB] p-4">
        <div className="flex gap-[10px]">
          {isGenerating ? (
            <>
              {/* 이미지 그리드 스켈레톤 */}
              <div className="relative h-[428px] w-[243px] overflow-hidden rounded-[10px] bg-gradient-to-br from-sky-200 via-indigo-300 to-purple-400">
                <div className="h-full w-full animate-skeleton bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
              </div>

              {/* 로딩 상태 텍스트 */}
              <div className="flex flex-col justify-between">
                {/* 4x2 그리드 스켈레톤 */}
                <div className="flex gap-1">
                  {Array.from({ length: 4 }, (_, index) => (
                    <div
                      key={index}
                      className="relative h-[115px] w-[65px] overflow-hidden rounded-[10px] bg-gradient-to-br from-sky-200 via-indigo-300 to-purple-400"
                    >
                      <div className="h-full w-full animate-skeleton bg-gradient-to-r from-transparent via-white/40 to-transparent"></div>
                    </div>
                  ))}
                </div>

                {/* 로딩 상태 텍스트 */}
                <div className="mt-4 flex flex-col items-center gap-2">
                  {currentRequestId && (
                    <p className="text-xs text-[#8F929B]">예상 시간: 10-30초</p>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              {/* 왼쪽 전체 이미지 */}
              <div className="flex justify-between gap-4">
                {clothingData.generatedImages.length > 0 && (
                  <>
                    <div className="relative h-[428px] w-[243px] shrink-0 rounded-[10px] bg-gray-200 hover:relative hover:cursor-pointer hover:opacity-70">
                      <img
                        src={convertStorageUrlToCdn(
                          clothingData.generatedImages[selectedVariation]
                            .imgUrl,
                        )}
                        alt="생성된 스타일 이미지"
                        className="h-full w-full object-cover"
                        onClick={() => {
                          handleShowImageSlide()
                        }}
                        onMouseOver={() => {
                          setIsHover(true)
                        }}
                        onMouseLeave={() => {
                          setIsHover(false)
                        }}
                      />
                      {/* 전체보기 버튼 */}

                      {isHover && (
                        <span className="absolute left-[50%] top-[50%]"></span>
                      )}
                    </div>

                    <div className="flex flex-col justify-between">
                      <div className="flex flex-wrap gap-[5px]">
                        {clothingData.generatedImages.length > 0 &&
                          clothingData.generatedImages.map(
                            (imageUrl, index) => (
                              <div
                                key={index}
                                className={classNames(
                                  'h-[115px] w-[65px] cursor-pointer rounded-[8px] transition-all',
                                  {
                                    'relative outline outline-[4px] outline-main':
                                      selectedVariation === index,
                                    'border-transparent':
                                      selectedVariation !== index,
                                  },
                                )}
                                onClick={() => {
                                  setSelectedVariation(index)
                                  setClothingData((prev) => {
                                    return {
                                      ...prev,
                                      selectedGarmentJobId: imageUrl.id,
                                    }
                                  })
                                }}
                              >
                                <img
                                  src={convertStorageUrlToCdn(imageUrl.imgUrl)}
                                  alt={`변형 이미지 ${index + 1}`}
                                  className="h-full w-full rounded-[4px] object-cover"
                                />
                                {selectedVariation === index && (
                                  <div className="absolute bottom-0 left-0 flex h-[28px] w-full items-center justify-center bg-main font-bold text-white">
                                    <span>선택</span>
                                  </div>
                                )}
                              </div>
                            ),
                          )}
                      </div>
                      {/* 하단 컨트롤 섹션 */}
                      <div className="flex w-full flex-col justify-between gap-[10px]">
                        {/* 좌측 - 안내 텍스트 */}
                        <div className="flex-1">
                          <p className="text-sm font-bold leading-tight text-[#FD6767]">
                            💃🏻 <br />
                            <span className="text-black">
                              혹시 옷 입히기
                              <br />
                              어려우세요?
                            </span>
                            <br />
                            <button
                              onClick={() => setIsGuideModalOpen(true)}
                              className="cursor-pointer text-[#FD6767] hover:underline"
                            >
                              이것부터 확인하세요! &gt;
                            </button>
                          </p>
                        </div>

                        {/* 우측 - 버튼 */}
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-col items-center justify-center gap-2 rounded-xl bg-[#EEF1F7] p-2">
                            <p className="text-[14px]">
                              옷 사진을 교체해보는게 가장 효과적!
                            </p>
                            <button
                              onClick={handleReplaceClothing}
                              className="flex h-[44px] w-full items-center justify-center gap-1 rounded-[10px] bg-[#D9DEE8] text-sm font-bold text-[#5E6068] transition-colors"
                            >
                              <Shirt size={16} />옷 사진 교체
                            </button>
                          </div>
                          <div className="flex flex-col items-center justify-center gap-2 rounded-xl bg-[#EEF1F7] p-2">
                            <p className="text-center text-[14px]">
                              {' '}
                              핏, 기장감, 이너, 액세서리 설명하면
                              <br />
                              원하는 느낌대로 입히기 가능!
                            </p>
                            <button
                              onClick={() => {
                                if (onGoToStep) {
                                  onGoToStep(4)
                                }
                              }}
                              className="flex h-[44px] w-full items-center justify-center gap-1 rounded-[10px] bg-[#D9DEE8] text-sm font-bold text-[#5E6068] transition-colors"
                            >
                              <Pencil />옷 특징 설명 수정
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {clothingData.generatedImages.length === 0 &&
                (requestStatus === 'failed' || compositionError) && (
                  <>
                    <div className="flex h-[428px] w-[243px] shrink-0 flex-col items-center justify-center gap-[10px] rounded-[10px] bg-[#2E343D] p-5">
                      <EyeOffIcon className="h-8 w-8 text-white" />
                      <div className="text-center">
                        <div className="mb-1 text-sm font-bold text-white">
                          생성 실패
                        </div>
                        <div className="text-sm text-[#B6BBC6]">
                          옷 이미지에 문제가 생긴 것 같아요.
                          <br />옷 사진을 교체해보세요!
                        </div>
                      </div>
                    </div>
                    <div className="just flex flex-col justify-end gap-[10px]">
                      {/* 좌측 - 안내 텍스트 */}

                      <div className="flex flex-col gap-2">
                        <div className="flex-1">
                          <p className="text-sm font-bold leading-tight text-[#FD6767]">
                            💃🏻 <br />
                            <span className="text-black">
                              혹시 옷 입히기
                              <br />
                              어려우세요?
                            </span>
                            <br />
                            <button
                              onClick={() => setIsGuideModalOpen(true)}
                              className="cursor-pointer text-[#FD6767] hover:underline"
                            >
                              이것부터 확인하세요! &gt;
                            </button>
                          </p>
                        </div>
                        <div className="flex flex-col items-center justify-center gap-2 rounded-xl bg-[#EEF1F7] p-2">
                          <p className="text-[14px]">
                            옷 사진을 교체해보는게 가장 효과적!
                          </p>
                          <button
                            onClick={handleReplaceClothing}
                            className="flex h-[44px] w-full items-center justify-center gap-1 rounded-[10px] bg-[#D9DEE8] text-sm font-bold text-[#5E6068] transition-colors"
                          >
                            <Shirt size={16} />옷 사진 교체
                          </button>
                        </div>
                        <div className="flex flex-col items-center justify-center gap-2 rounded-xl bg-[#EEF1F7] p-2">
                          <p className="text-center">
                            {' '}
                            핏, 기장감, 이너, 액세서리 설명하면
                            <br />
                            원하는 느낌대로 입히기 가능!
                          </p>
                          <button
                            onClick={() => {
                              if (onGoToStep) {
                                onGoToStep(4)
                              }
                            }}
                            className="flex h-[44px] w-full items-center justify-center gap-1 rounded-[10px] bg-[#D9DEE8] text-sm font-bold text-[#5E6068] transition-colors"
                          >
                            <Pencil />옷 특징 설명 수정
                          </button>
                        </div>

                        {(compositionError ||
                          clothingData.sampleRequestStatus === 'failed') && (
                          <button
                            onClick={handleRegenerate}
                            className="flex h-[44px] w-[160px] items-center justify-center gap-1 rounded-[10px] bg-[#EEF1F7] px-6 py-2 text-sm font-bold text-[#5E6068] transition-colors hover:bg-[#E5E7EB]"
                          >
                            <RotateCw size={16} />
                            재생성
                          </button>
                        )}
                      </div>
                    </div>
                  </>
                )}
            </>
          )}
        </div>
      </div>

      {/* 옷 입히기 가이드 모달 */}
      <ClothingGuideModal
        isOpen={isGuideModalOpen}
        onClose={() => setIsGuideModalOpen(false)}
      />

      {/* 전체 이미지 모달 */}
      <FullImageModal
        isOpen={isFullModalOpen}
        onClose={() => setIsFullModalOpen(false)}
        id={getCurrentJobId()}
      />

      {/* 이미지 슬라이드 모달 */}
      <ImageSlideModal
        isOpen={isImageSlideModalOpen}
        onClose={() => setIsImageSlideModalOpen(false)}
        images={clothingData.generatedImages.map((el) => {
          return el.imgUrl
        })}
        initialIndex={selectedVariation}
      />
    </div>
  )
}
